import { useState, useEffect, useRef, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useDataStore } from "@/lib/store/data-store";
import type { Unit } from "@/lib/types/unit";
import { Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { normalizeSearchText, getDescriptorSearchText } from "@/lib/utils/search-normalize";

interface UnitSelectorProps {
  onSelect: (unit: Unit) => void;
  onRemove: (unit: Unit) => void;
  selectedUnits: Unit[];
}

export function UnitSelector({ onSelect, onRemove, selectedUnits }: UnitSelectorProps) {
  const { units, loadingUnits } = useDataStore();
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());

  // Filter units based on query, excluding already selected units
  const filteredUnits = useMemo(() => {
    if (!units || !query.trim()) return [];

    const normalizedQuery = normalizeSearchText(query);
    const selectedDescriptors = new Set(selectedUnits.map((u) => u.descriptorName));

    return units
      .filter((unit) => {
        if (selectedDescriptors.has(unit.descriptorName)) return false;

        const normalizedName = normalizeSearchText(unit.name);
        const normalizedDescriptor = normalizeSearchText(getDescriptorSearchText(unit.descriptorName));
        const nameMatch = normalizedName.includes(normalizedQuery);
        const descriptorMatch = normalizedDescriptor.includes(normalizedQuery);
        return nameMatch || descriptorMatch;
      })
      .slice(0, 10);
  }, [units, query, selectedUnits]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredUnits]);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const selectedItem = itemRefs.current.get(selectedIndex);
      if (selectedItem) {
        selectedItem.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, [selectedIndex, isOpen]);

  useEffect(() => {
    setIsOpen(query.trim().length > 0 && filteredUnits.length > 0);
  }, [query, filteredUnits]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  const handleSelectUnit = (unit: Unit) => {
    onSelect(unit);
    setQuery("");
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || filteredUnits.length === 0) {
      if (e.key === "Enter" && query.trim()) {
        const normalizedQuery = normalizeSearchText(query);
        const exactMatch = units?.find(
          (unit) =>
            !selectedUnits.some((u) => u.descriptorName === unit.descriptorName) &&
            (normalizeSearchText(unit.name) === normalizedQuery ||
              normalizeSearchText(getDescriptorSearchText(unit.descriptorName)) === normalizedQuery)
        );
        if (exactMatch) {
          handleSelectUnit(exactMatch);
        }
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % filteredUnits.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + filteredUnits.length) % filteredUnits.length);
        break;
      case "Enter":
        e.preventDefault();
        if (filteredUnits[selectedIndex]) {
          handleSelectUnit(filteredUnits[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setQuery("");
        break;
    }
  };

  return (
    <div className="space-y-2">
      <div ref={containerRef} className="relative w-full">
        <div className="relative">
          <Input
            ref={inputRef}
            type="text"
            placeholder="Search for unit to compare..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (query.trim().length > 0 && filteredUnits.length > 0) {
                setIsOpen(true);
              }
            }}
            className="w-full pr-10"
          />
          {loadingUnits && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>

        {isOpen && filteredUnits.length > 0 ? (
          <div
            ref={dropdownRef}
            className="absolute left-0 right-0 top-full z-[70] mt-1 touch-pan-y overscroll-contain rounded-md border bg-popover text-popover-foreground shadow-md max-h-[min(300px,50vh)] overflow-y-auto"
          >
            {filteredUnits.map((unit, index) => (
              <button
                key={unit.descriptorName}
                ref={(el) => {
                  if (el) {
                    itemRefs.current.set(index, el);
                  } else {
                    itemRefs.current.delete(index);
                  }
                }}
                type="button"
                onClick={() => handleSelectUnit(unit)}
                className={cn(
                  "w-full text-left px-4 py-2 hover:bg-accent hover:text-accent-foreground transition-colors",
                  index === selectedIndex && "bg-accent text-accent-foreground"
                )}
              >
                <div className="font-medium">{unit.name}</div>
                <div className="text-xs text-muted-foreground">{unit.descriptorName}</div>
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {selectedUnits.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedUnits.map((unit) => (
            <Badge key={unit.descriptorName} variant="secondary" className="px-2 py-1">
              {unit.name}
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 ml-2 hover:bg-transparent"
                onClick={() => onRemove(unit)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
